/**
 * Document Templates for Credit Enhancement & Financing
 * Includes: Submission Memorandum, Bank PAS Summary, Acceptance Forms
 * For both Auto (TIAO) and Mortgage (TIMO) financing
 */

// Auto Financing Submission Memorandum
export const generateAutoSubmissionMemorandum = (data) => {
  const today = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', month: 'long', day: 'numeric' 
  });
  
  return `
    <div class="page" style="background: white; padding: 40px; font-family: 'Times New Roman', serif; color: #1a1a1a; min-height: 1100px;">
      <div style="text-align: center; border-bottom: 3px double #b8860b; padding-bottom: 20px; margin-bottom: 30px;">
        <h1 style="font-size: 18px; margin: 0; letter-spacing: 3px; color: #0a1628;">BETHEREUM PRIVATE WEALTH MANAGEMENT</h1>
        <p style="font-size: 11px; color: #666; margin: 5px 0 0 0;">Treasury-Indexed Auto Obligation (TIAO) Program</p>
      </div>
      
      <h2 style="text-align: center; font-size: 16px; margin-bottom: 5px; color: #0a1628;">
        CREDIT ENHANCEMENT & FINANCING SUBMISSION MEMORANDUM
      </h2>
      <p style="text-align: center; font-size: 11px; color: #666; margin-bottom: 30px;">
        <strong>Confidential – For Credit Review Only</strong>
      </p>
      
      <div style="background: #f8f9fa; border-left: 4px solid #b8860b; padding: 15px; margin-bottom: 25px;">
        <p style="margin: 0; font-size: 12px;"><strong>Document ID:</strong> ${data.memorandum_id || 'MEM-' + Date.now().toString(36).toUpperCase()}</p>
        <p style="margin: 5px 0 0 0; font-size: 12px;"><strong>Date:</strong> ${today}</p>
        <p style="margin: 5px 0 0 0; font-size: 12px;"><strong>Reference Agreement:</strong> ${data.agreement_id || 'N/A'}</p>
      </div>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px;">1. PURPOSE OF SUBMISSION</h3>
      <p style="font-size: 11px; line-height: 1.6; text-align: justify;">
        This memorandum accompanies a private financing and credit enhancement package submitted for lender or dealer credit review. The package is intended to support:
      </p>
      <ul style="font-size: 11px; line-height: 1.8; margin-left: 20px;">
        <li>Vehicle financing or refinancing</li>
        <li>Loan assumption or novation</li>
        <li>Credit risk mitigation for an existing or new obligation</li>
      </ul>
      <p style="font-size: 11px; line-height: 1.6; text-align: justify; background: #fff3cd; padding: 10px; border-radius: 4px;">
        <strong>NOTICE:</strong> This submission does <strong>not</strong> constitute a demand for payment, a negotiable instrument, or a representation of guaranteed performance.
      </p>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">2. TRANSACTION OVERVIEW</h3>
      <table style="width: 100%; font-size: 11px; border-collapse: collapse; margin: 15px 0;">
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; width: 35%;"><strong>Borrower:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.borrower_name || 'N/A'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Asset:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.vehicle_description || 'Motor Vehicle'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>VIN:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd; font-family: monospace;">${data.vehicle_vin || 'Provided under separate schedule'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Financing Source:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">Dealer-arranged or direct lender</td>
        </tr>
      </table>
      
      <p style="font-size: 11px; margin: 15px 0;"><strong>Requested Action:</strong></p>
      <div style="font-size: 11px; margin-left: 20px;">
        <p style="margin: 5px 0;">☐ New financing</p>
        <p style="margin: 5px 0;">☐ Refinance</p>
        <p style="margin: 5px 0;">☑ Loan assumption / novation</p>
        <p style="margin: 5px 0;">☐ Credit-supported settlement</p>
      </div>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">3. CREDIT ENHANCEMENT STRUCTURE</h3>
      <p style="font-size: 11px; line-height: 1.6; text-align: justify;">
        The financing is supported by a <strong>Performance Assurance Structure (PAS)</strong>, which is a private contractual credit enhancement designed to support borrower performance and mitigate loss exposure.
      </p>
      <div style="background: #e8f4fd; padding: 15px; border-radius: 4px; margin: 15px 0;">
        <p style="font-size: 11px; margin: 0 0 10px 0;"><strong>Key Characteristics:</strong></p>
        <ul style="font-size: 11px; line-height: 1.6; margin: 0 0 0 20px; padding: 0;">
          <li>Non-payment contractual support</li>
          <li>Defined triggers and cure periods</li>
          <li>Limited exposure and duration</li>
          <li>No guarantee of payment</li>
        </ul>
      </div>
      <p style="font-size: 11px; line-height: 1.6; background: #f8d7da; padding: 10px; border-radius: 4px;">
        <strong>IMPORTANT:</strong> The PAS does not replace borrower liability and does not constitute insurance, a surety, or a bank instrument.
      </p>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">4. INSTRUMENTS INCLUDED IN THIS PACKAGE</h3>
      <p style="font-size: 11px; margin-bottom: 10px;">The following documents are provided for review:</p>
      <ol style="font-size: 11px; line-height: 1.8; margin-left: 20px;">
        <li>Performance Assurance Agreement (PAS)</li>
        <li>Bank-Facing PAS Summary</li>
        <li>Proposed Finance Agreement (if applicable)</li>
        <li>Proposed Novation Agreement (if applicable)</li>
        <li>Disclosure & Risk Acknowledgements</li>
      </ol>
      <p style="font-size: 11px; margin-top: 10px;"><em>Optional (by lender request):</em></p>
      <ul style="font-size: 11px; margin-left: 20px;">
        <li>Tokenized check or draft instrument (non-negotiable, conditional)</li>
      </ul>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">5. USE OF TOKENIZED OR DRAFT INSTRUMENTS</h3>
      <p style="font-size: 11px; line-height: 1.6; text-align: justify;">Any tokenized check, draft check, or payment instruction provided:</p>
      <ul style="font-size: 11px; line-height: 1.6; margin-left: 20px;">
        <li>Is <strong>not</strong> legal tender</li>
        <li>Is <strong>not</strong> payable on demand</li>
        <li>Is conditional upon acceptance of the underlying finance or novation agreement</li>
      </ul>
      <p style="font-size: 11px; background: #fff3cd; padding: 10px; border-radius: 4px;">
        Such instruments are provided solely as <strong>evidence of structured consideration</strong>, not as immediate payment.
      </p>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">6. ACCOUNTING & SETTLEMENT POSITION</h3>
      <ul style="font-size: 11px; line-height: 1.6; margin-left: 20px;">
        <li>No debt is settled or discharged by submission alone</li>
        <li>Any payoff, restructuring, or lien release requires a separate executed agreement</li>
        <li>Lender retains full underwriting and approval discretion</li>
      </ul>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">7. REGULATORY POSITIONING</h3>
      <ul style="font-size: 11px; line-height: 1.6; margin-left: 20px;">
        <li>No banking, insurance, or broker-dealer services are being offered</li>
        <li>No funds are held on behalf of the lender</li>
        <li>No public solicitation or securities offering is made</li>
      </ul>
      <p style="font-size: 11px; margin-top: 10px;">This is a private, bilateral or multilateral credit transaction.</p>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">8. IMPORTANT DISCLOSURES</h3>
      <div style="background: #f8f9fa; padding: 15px; border: 1px solid #ddd; border-radius: 4px;">
        <p style="font-size: 11px; margin: 0; line-height: 1.6;">
          This memorandum is informational only and non-binding. Rights and obligations arise only upon execution of definitive agreements.
        </p>
      </div>
      
      <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #b8860b;">
        <p style="font-size: 11px; margin: 0;"><strong>Submitted By:</strong> Bethereum Private Wealth Management</p>
        <p style="font-size: 11px; margin: 5px 0;"><strong>Document Classification:</strong> Credit Review Memorandum</p>
        <p style="font-size: 10px; color: #666; margin-top: 15px;">
          All questions, credit conditions, or documentation requests should be directed to the transaction administrator.
        </p>
      </div>
    </div>
  `;
};

// Auto Financing Dealer Acceptance Form
export const generateAutoAcceptanceForm = (data) => {
  const today = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', month: 'long', day: 'numeric' 
  });
  
  return `
    <div class="page" style="background: white; padding: 40px; font-family: 'Times New Roman', serif; color: #1a1a1a; min-height: 1100px;">
      <div style="text-align: center; border-bottom: 3px double #b8860b; padding-bottom: 20px; margin-bottom: 30px;">
        <h1 style="font-size: 18px; margin: 0; letter-spacing: 3px; color: #0a1628;">BETHEREUM PRIVATE WEALTH MANAGEMENT</h1>
        <p style="font-size: 11px; color: #666; margin: 5px 0 0 0;">Treasury-Indexed Auto Obligation (TIAO) Program</p>
      </div>
      
      <h2 style="text-align: center; font-size: 16px; margin-bottom: 5px; color: #0a1628;">
        DEALER / CREDITOR ACCEPTANCE & ACKNOWLEDGMENT FORM
      </h2>
      <p style="text-align: center; font-size: 11px; color: #666; margin-bottom: 10px;">
        (Private Credit & Credit Enhancement Transaction)
      </p>
      <p style="text-align: center; font-size: 10px; color: #999; margin-bottom: 30px;">
        <strong>Confidential – Credit Review Use Only</strong>
      </p>
      
      <div style="background: #f8f9fa; border-left: 4px solid #b8860b; padding: 15px; margin-bottom: 25px;">
        <p style="margin: 0; font-size: 12px;"><strong>Form ID:</strong> ${data.form_id || 'ACK-' + Date.now().toString(36).toUpperCase()}</p>
        <p style="margin: 5px 0 0 0; font-size: 12px;"><strong>Date:</strong> ${today}</p>
        <p style="margin: 5px 0 0 0; font-size: 12px;"><strong>Reference Agreement:</strong> ${data.agreement_id || 'N/A'}</p>
      </div>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px;">1. PURPOSE OF THIS FORM</h3>
      <p style="font-size: 11px; line-height: 1.6; text-align: justify;">
        This Acceptance & Acknowledgment Form ("Form") is executed to evidence review and conditional acceptance of a <strong>private credit structure</strong> supported by a <strong>Performance Assurance Structure (PAS)</strong> and, where applicable, a <strong>private finance or novation agreement</strong>.
      </p>
      <p style="font-size: 11px; background: #e8f4fd; padding: 10px; border-radius: 4px; margin-top: 10px;">
        This Form is <strong>not</strong> a vendor agreement, lender onboarding document, broker agreement, or commitment to fund.
      </p>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">2. ACCEPTING PARTY</h3>
      <div style="font-size: 11px; margin: 15px 0;">
        <p style="margin: 5px 0;">☐ Dealer</p>
        <p style="margin: 5px 0;">☑ Creditor / Lender</p>
        <p style="margin: 5px 0;">☐ Captive Finance Arm</p>
      </div>
      <table style="width: 100%; font-size: 11px; border-collapse: collapse; margin: 15px 0;">
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; width: 35%;"><strong>Legal Name:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.creditor_name || '___________________________'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Address:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.creditor_address || '___________________________'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Authorized Signatory & Title:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">___________________________</td>
        </tr>
      </table>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">3. TRANSACTION REFERENCE</h3>
      <table style="width: 100%; font-size: 11px; border-collapse: collapse; margin: 15px 0;">
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; width: 35%;"><strong>Borrower / Obligor:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.borrower_name || 'N/A'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Asset Type:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">☑ Auto ☐ Real Property ☐ Other</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Asset Identifier (VIN):</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd; font-family: monospace;">${data.vehicle_vin || 'N/A'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Settlement Amount:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd; color: #b8860b; font-weight: bold;">$${(data.settlement_amount || 0).toLocaleString()}</td>
        </tr>
      </table>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">4. DOCUMENTS REVIEWED</h3>
      <p style="font-size: 11px; margin-bottom: 10px;">The undersigned acknowledges receipt and review of the following (as applicable):</p>
      <div style="font-size: 11px; margin-left: 20px;">
        <p style="margin: 5px 0;">☑ Credit Enhancement Submission Memorandum</p>
        <p style="margin: 5px 0;">☑ Bank-Facing PAS Summary</p>
        <p style="margin: 5px 0;">☑ Performance Assurance Agreement (PAS)</p>
        <p style="margin: 5px 0;">☐ Private Finance Agreement</p>
        <p style="margin: 5px 0;">☑ Novation & Assumption Agreement</p>
        <p style="margin: 5px 0;">☐ Draft / Tokenized Instrument Disclosure</p>
      </div>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">5. ACKNOWLEDGMENTS</h3>
      <p style="font-size: 11px; margin-bottom: 10px;">The undersigned expressly acknowledges and agrees that:</p>
      <ol style="font-size: 11px; line-height: 1.8; margin-left: 20px;">
        <li>The PAS is a <strong>contractual, conditional performance support arrangement</strong> only.</li>
        <li>The PAS is <strong>not</strong> a guarantee, surety, insurance policy, letter of credit, or bank obligation.</li>
        <li>Any draft check, tokenized check, or payment instruction is <strong>conditional, non-negotiable</strong>, and <strong>not legal tender</strong>.</li>
        <li>No debt is paid, settled, or discharged absent a <strong>separately executed written agreement</strong>.</li>
        <li>Acceptance of this structure is <strong>discretionary</strong> and subject to independent credit judgment.</li>
      </ol>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">6. NO RELIANCE & NO REGULATORY REPRESENTATION</h3>
      <p style="font-size: 11px; margin-bottom: 10px;">The undersigned confirms:</p>
      <ul style="font-size: 11px; line-height: 1.6; margin-left: 20px;">
        <li>No reliance is placed on the PAS as payment or guaranty</li>
        <li>No banking, insurance, or fiduciary relationship is created</li>
        <li>No regulatory approvals are implied or required by acceptance</li>
      </ul>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">7. NO OBLIGATION TO PROCEED</h3>
      <div style="background: #fff3cd; padding: 15px; border-radius: 4px;">
        <p style="font-size: 11px; margin: 0; line-height: 1.6;">Execution of this Form:</p>
        <ul style="font-size: 11px; line-height: 1.6; margin: 10px 0 0 20px;">
          <li>Does <strong>not</strong> obligate funding, settlement, or lien release</li>
          <li>Does <strong>not</strong> bind any party absent definitive agreements</li>
          <li>Serves solely as acknowledgment of review and understanding</li>
        </ul>
      </div>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">8. GOVERNING LAW</h3>
      <p style="font-size: 11px; line-height: 1.6;">
        This Form shall be governed by the laws of the State of <strong>Delaware</strong>, without regard to conflict of laws principles.
      </p>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">9. SIGNATURE</h3>
      <p style="font-size: 11px; margin-bottom: 15px;">
        By signing below, the undersigned certifies authority to bind the accepting entity for acknowledgment purposes only.
      </p>
      <table style="width: 100%; font-size: 11px; border-collapse: collapse;">
        <tr>
          <td style="padding: 10px 0;"><strong>Accepting Entity:</strong></td>
          <td style="padding: 10px 0; border-bottom: 1px solid #333; width: 60%;"></td>
        </tr>
        <tr>
          <td style="padding: 10px 0;"><strong>Authorized Signatory:</strong></td>
          <td style="padding: 10px 0; border-bottom: 1px solid #333;"></td>
        </tr>
        <tr>
          <td style="padding: 10px 0;"><strong>Title:</strong></td>
          <td style="padding: 10px 0; border-bottom: 1px solid #333;"></td>
        </tr>
        <tr>
          <td style="padding: 10px 0;"><strong>Signature:</strong></td>
          <td style="padding: 10px 0; border-bottom: 1px solid #333;"></td>
        </tr>
        <tr>
          <td style="padding: 10px 0;"><strong>Date:</strong></td>
          <td style="padding: 10px 0; border-bottom: 1px solid #333;"></td>
        </tr>
      </table>
    </div>
  `;
};

// Mortgage Submission Memorandum
export const generateMortgageSubmissionMemorandum = (data) => {
  const today = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', month: 'long', day: 'numeric' 
  });
  
  return `
    <div class="page" style="background: white; padding: 40px; font-family: 'Times New Roman', serif; color: #1a1a1a; min-height: 1100px;">
      <div style="text-align: center; border-bottom: 3px double #6366f1; padding-bottom: 20px; margin-bottom: 30px;">
        <h1 style="font-size: 18px; margin: 0; letter-spacing: 3px; color: #0a1628;">BETHEREUM PRIVATE WEALTH MANAGEMENT</h1>
        <p style="font-size: 11px; color: #666; margin: 5px 0 0 0;">Treasury-Indexed Mortgage Obligation (TIMO) Program</p>
      </div>
      
      <h2 style="text-align: center; font-size: 16px; margin-bottom: 5px; color: #0a1628;">
        CREDIT ENHANCEMENT & MORTGAGE REFINANCE SUBMISSION MEMORANDUM
      </h2>
      <p style="text-align: center; font-size: 11px; color: #666; margin-bottom: 30px;">
        <strong>Confidential – For Credit Review Only</strong>
      </p>
      
      <div style="background: #f8f9fa; border-left: 4px solid #6366f1; padding: 15px; margin-bottom: 25px;">
        <p style="margin: 0; font-size: 12px;"><strong>Document ID:</strong> ${data.memorandum_id || 'MEM-MORT-' + Date.now().toString(36).toUpperCase()}</p>
        <p style="margin: 5px 0 0 0; font-size: 12px;"><strong>Date:</strong> ${today}</p>
        <p style="margin: 5px 0 0 0; font-size: 12px;"><strong>Reference Agreement:</strong> ${data.agreement_id || 'N/A'}</p>
      </div>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px;">1. PURPOSE OF SUBMISSION</h3>
      <p style="font-size: 11px; line-height: 1.6; text-align: justify;">
        This memorandum accompanies a private mortgage financing and credit enhancement package submitted for lender credit review. The package is intended to support:
      </p>
      <ul style="font-size: 11px; line-height: 1.8; margin-left: 20px;">
        <li>Mortgage refinancing or restructuring</li>
        <li>Loan assumption or novation</li>
        <li>Credit risk mitigation for an existing mortgage obligation</li>
        <li>Lien release coordination</li>
      </ul>
      <p style="font-size: 11px; line-height: 1.6; text-align: justify; background: #fff3cd; padding: 10px; border-radius: 4px;">
        <strong>NOTICE:</strong> This submission does <strong>not</strong> constitute a demand for payment, a negotiable instrument, or a representation of guaranteed performance.
      </p>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">2. TRANSACTION OVERVIEW</h3>
      <table style="width: 100%; font-size: 11px; border-collapse: collapse; margin: 15px 0;">
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; width: 35%;"><strong>Borrower:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.borrower_name || 'N/A'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Property Type:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.property_type || 'Residential Real Property'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Property Address:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.property_address || 'N/A'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Current Lender:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.original_lender_name || 'N/A'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Current Balance:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">$${(data.current_balance || 0).toLocaleString()}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Payoff Amount:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd; color: #6366f1; font-weight: bold;">$${(data.payoff_amount || 0).toLocaleString()}</td>
        </tr>
      </table>
      
      <p style="font-size: 11px; margin: 15px 0;"><strong>Requested Action:</strong></p>
      <div style="font-size: 11px; margin-left: 20px;">
        <p style="margin: 5px 0;">☐ New mortgage financing</p>
        <p style="margin: 5px 0;">☑ Mortgage refinance / restructure</p>
        <p style="margin: 5px 0;">☑ Loan assumption / novation</p>
        <p style="margin: 5px 0;">☐ Credit-supported payoff</p>
      </div>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">3. CREDIT ENHANCEMENT STRUCTURE</h3>
      <p style="font-size: 11px; line-height: 1.6; text-align: justify;">
        The mortgage refinancing is supported by a <strong>Performance Assurance Structure (PAS)</strong>, which is a private contractual credit enhancement designed to support borrower performance and mitigate loss exposure for the existing mortgagee.
      </p>
      <div style="background: #e8f4fd; padding: 15px; border-radius: 4px; margin: 15px 0;">
        <p style="font-size: 11px; margin: 0 0 10px 0;"><strong>Key Characteristics:</strong></p>
        <ul style="font-size: 11px; line-height: 1.6; margin: 0 0 0 20px; padding: 0;">
          <li>Non-payment contractual support backed by BETH collateral</li>
          <li>Defined triggers and cure periods</li>
          <li>Limited exposure and duration aligned with payoff timeline</li>
          <li>No guarantee of payment - conditional performance support only</li>
        </ul>
      </div>
      <p style="font-size: 11px; line-height: 1.6; background: #f8d7da; padding: 10px; border-radius: 4px;">
        <strong>IMPORTANT:</strong> The PAS does not replace borrower liability and does not constitute insurance, a surety, or a bank instrument. Lender consent is required for novation.
      </p>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">4. INSTRUMENTS INCLUDED IN THIS PACKAGE</h3>
      <p style="font-size: 11px; margin-bottom: 10px;">The following documents are provided for mortgagee review:</p>
      <ol style="font-size: 11px; line-height: 1.8; margin-left: 20px;">
        <li>Performance Assurance Agreement (PAS) - Mortgage Edition</li>
        <li>Bank-Facing PAS Summary for Mortgagees</li>
        <li>Proposed Novation Agreement</li>
        <li>Treasury-Indexed Payment Schedule</li>
        <li>BETH Collateral Verification Certificate</li>
        <li>Disclosure & Risk Acknowledgements</li>
      </ol>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">5. LIEN RELEASE REQUIREMENTS</h3>
      <p style="font-size: 11px; line-height: 1.6; text-align: justify;">
        Upon execution of the novation agreement and receipt of the structured payoff:
      </p>
      <ul style="font-size: 11px; line-height: 1.6; margin-left: 20px;">
        <li>Original mortgagee shall execute satisfaction of mortgage</li>
        <li>Lien release shall be recorded in the appropriate county</li>
        <li>New deed of trust (if applicable) shall be recorded in favor of Bethereum Credit Structure</li>
      </ul>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">6. ACCOUNTING & SETTLEMENT POSITION</h3>
      <ul style="font-size: 11px; line-height: 1.6; margin-left: 20px;">
        <li>No debt is settled or discharged by submission alone</li>
        <li>Payoff requires separately executed novation agreement</li>
        <li>Lender retains full approval discretion</li>
        <li>Title insurance coordination required</li>
      </ul>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">7. REGULATORY POSITIONING</h3>
      <ul style="font-size: 11px; line-height: 1.6; margin-left: 20px;">
        <li>This is a private, bilateral credit restructuring transaction</li>
        <li>No RESPA, TILA, or consumer lending regulations apply to this private arrangement</li>
        <li>No banking, insurance, or broker-dealer services are being offered</li>
        <li>All parties are sophisticated, accredited investors</li>
      </ul>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">8. IMPORTANT DISCLOSURES</h3>
      <div style="background: #f8f9fa; padding: 15px; border: 1px solid #ddd; border-radius: 4px;">
        <p style="font-size: 11px; margin: 0; line-height: 1.6;">
          This memorandum is informational only and non-binding. Rights and obligations arise only upon execution of definitive agreements. Borrower remains liable under existing mortgage until novation is complete.
        </p>
      </div>
      
      <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #6366f1;">
        <p style="font-size: 11px; margin: 0;"><strong>Submitted By:</strong> Bethereum Private Wealth Management - Mortgage Division</p>
        <p style="font-size: 11px; margin: 5px 0;"><strong>Document Classification:</strong> Mortgage Credit Review Memorandum</p>
      </div>
    </div>
  `;
};

// Mortgage Mortgagee Acceptance Form
export const generateMortgageAcceptanceForm = (data) => {
  const today = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', month: 'long', day: 'numeric' 
  });
  
  return `
    <div class="page" style="background: white; padding: 40px; font-family: 'Times New Roman', serif; color: #1a1a1a; min-height: 1100px;">
      <div style="text-align: center; border-bottom: 3px double #6366f1; padding-bottom: 20px; margin-bottom: 30px;">
        <h1 style="font-size: 18px; margin: 0; letter-spacing: 3px; color: #0a1628;">BETHEREUM PRIVATE WEALTH MANAGEMENT</h1>
        <p style="font-size: 11px; color: #666; margin: 5px 0 0 0;">Treasury-Indexed Mortgage Obligation (TIMO) Program</p>
      </div>
      
      <h2 style="text-align: center; font-size: 16px; margin-bottom: 5px; color: #0a1628;">
        MORTGAGEE ACCEPTANCE & ACKNOWLEDGMENT FORM
      </h2>
      <p style="text-align: center; font-size: 11px; color: #666; margin-bottom: 10px;">
        (Private Credit & Credit Enhancement Transaction)
      </p>
      <p style="text-align: center; font-size: 10px; color: #999; margin-bottom: 30px;">
        <strong>Confidential – Credit Review Use Only</strong>
      </p>
      
      <div style="background: #f8f9fa; border-left: 4px solid #6366f1; padding: 15px; margin-bottom: 25px;">
        <p style="margin: 0; font-size: 12px;"><strong>Form ID:</strong> ${data.form_id || 'ACK-MORT-' + Date.now().toString(36).toUpperCase()}</p>
        <p style="margin: 5px 0 0 0; font-size: 12px;"><strong>Date:</strong> ${today}</p>
        <p style="margin: 5px 0 0 0; font-size: 12px;"><strong>Reference Agreement:</strong> ${data.agreement_id || 'N/A'}</p>
      </div>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px;">1. PURPOSE OF THIS FORM</h3>
      <p style="font-size: 11px; line-height: 1.6; text-align: justify;">
        This Acceptance & Acknowledgment Form ("Form") is executed to evidence review and conditional acceptance of a <strong>private mortgage restructuring</strong> supported by a <strong>Performance Assurance Structure (PAS)</strong> and a <strong>mortgage novation agreement</strong>.
      </p>
      <p style="font-size: 11px; background: #e8f4fd; padding: 10px; border-radius: 4px; margin-top: 10px;">
        This Form is <strong>not</strong> a satisfaction of mortgage, lien release, or commitment to release collateral.
      </p>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">2. ACCEPTING PARTY (MORTGAGEE)</h3>
      <table style="width: 100%; font-size: 11px; border-collapse: collapse; margin: 15px 0;">
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; width: 35%;"><strong>Mortgagee Legal Name:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.lender_name || '___________________________'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Address:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.lender_address || '___________________________'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Loan Number:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd; font-family: monospace;">${data.loan_number || '___________________________'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Authorized Signatory & Title:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">___________________________</td>
        </tr>
      </table>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">3. TRANSACTION REFERENCE</h3>
      <table style="width: 100%; font-size: 11px; border-collapse: collapse; margin: 15px 0;">
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; width: 35%;"><strong>Borrower / Mortgagor:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.borrower_name || 'N/A'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Property Address:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.property_address || 'N/A'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Property Type:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.property_type || 'Single Family Residence'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Current Balance:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">$${(data.current_balance || 0).toLocaleString()}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Payoff Amount:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd; color: #6366f1; font-weight: bold;">$${(data.payoff_amount || 0).toLocaleString()}</td>
        </tr>
      </table>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">4. DOCUMENTS REVIEWED</h3>
      <p style="font-size: 11px; margin-bottom: 10px;">The undersigned acknowledges receipt and review of the following:</p>
      <div style="font-size: 11px; margin-left: 20px;">
        <p style="margin: 5px 0;">☑ Credit Enhancement Submission Memorandum - Mortgage Edition</p>
        <p style="margin: 5px 0;">☑ Bank-Facing PAS Summary for Mortgagees</p>
        <p style="margin: 5px 0;">☑ Performance Assurance Agreement (PAS)</p>
        <p style="margin: 5px 0;">☑ Proposed Novation Agreement</p>
        <p style="margin: 5px 0;">☑ Treasury-Indexed Payment Schedule</p>
        <p style="margin: 5px 0;">☑ BETH Collateral Verification Certificate</p>
      </div>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">5. MORTGAGEE ACKNOWLEDGMENTS</h3>
      <p style="font-size: 11px; margin-bottom: 10px;">The undersigned mortgagee expressly acknowledges and agrees that:</p>
      <ol style="font-size: 11px; line-height: 1.8; margin-left: 20px;">
        <li>The PAS is a <strong>contractual, conditional performance support arrangement</strong> only.</li>
        <li>The PAS is <strong>not</strong> a guarantee, surety, insurance policy, letter of credit, or bank obligation.</li>
        <li>Consent to novation is <strong>discretionary</strong> and subject to independent credit review.</li>
        <li>No lien release or satisfaction shall occur until definitive payoff is received and verified.</li>
        <li>The mortgagor remains liable under the existing note until novation is complete.</li>
      </ol>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">6. CONSENT TO NOVATION (IF APPLICABLE)</h3>
      <div style="background: #e8f4fd; padding: 15px; border-radius: 4px; margin: 15px 0;">
        <p style="font-size: 11px; margin: 0 0 10px 0;">
          ☐ Mortgagee <strong>CONSENTS</strong> to the proposed novation subject to receipt of payoff funds
        </p>
        <p style="font-size: 11px; margin: 0 0 10px 0;">
          ☐ Mortgagee <strong>DECLINES</strong> the proposed novation
        </p>
        <p style="font-size: 11px; margin: 0;">
          ☐ Mortgagee requests <strong>ADDITIONAL INFORMATION</strong> before making a determination
        </p>
      </div>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">7. GOVERNING LAW</h3>
      <p style="font-size: 11px; line-height: 1.6;">
        This Form shall be governed by the laws of the State of <strong>${data.property_state || 'Delaware'}</strong>, without regard to conflict of laws principles.
      </p>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">8. SIGNATURE</h3>
      <p style="font-size: 11px; margin-bottom: 15px;">
        By signing below, the undersigned certifies authority to bind the mortgagee for acknowledgment purposes.
      </p>
      <table style="width: 100%; font-size: 11px; border-collapse: collapse;">
        <tr>
          <td style="padding: 10px 0;"><strong>Mortgagee Entity:</strong></td>
          <td style="padding: 10px 0; border-bottom: 1px solid #333; width: 60%;"></td>
        </tr>
        <tr>
          <td style="padding: 10px 0;"><strong>Authorized Signatory:</strong></td>
          <td style="padding: 10px 0; border-bottom: 1px solid #333;"></td>
        </tr>
        <tr>
          <td style="padding: 10px 0;"><strong>Title:</strong></td>
          <td style="padding: 10px 0; border-bottom: 1px solid #333;"></td>
        </tr>
        <tr>
          <td style="padding: 10px 0;"><strong>Signature:</strong></td>
          <td style="padding: 10px 0; border-bottom: 1px solid #333;"></td>
        </tr>
        <tr>
          <td style="padding: 10px 0;"><strong>Date:</strong></td>
          <td style="padding: 10px 0; border-bottom: 1px solid #333;"></td>
        </tr>
      </table>
      
      <div style="margin-top: 30px; padding: 15px; background: #fff3cd; border-radius: 4px;">
        <p style="font-size: 10px; margin: 0; text-align: center;">
          <strong>NOTICE:</strong> Return signed form to Bethereum Private Wealth Management. 
          This acknowledgment does not constitute a binding agreement or commitment.
        </p>
      </div>
    </div>
  `;
};

// Mortgage PAS-Backed Deed of Trust Template
export const generateMortgageDeedOfTrust = (data) => {
  const today = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', month: 'long', day: 'numeric' 
  });
  
  return `
    <div class="page" style="background: white; padding: 40px; font-family: 'Times New Roman', serif; color: #1a1a1a; min-height: 1100px;">
      <div style="text-align: center; border-bottom: 3px double #6366f1; padding-bottom: 20px; margin-bottom: 30px;">
        <h1 style="font-size: 18px; margin: 0; letter-spacing: 3px; color: #0a1628;">BETHEREUM PRIVATE WEALTH MANAGEMENT</h1>
        <p style="font-size: 11px; color: #666; margin: 5px 0 0 0;">Treasury-Indexed Mortgage Obligation (TIMO) Program</p>
      </div>
      
      <h2 style="text-align: center; font-size: 16px; margin-bottom: 5px; color: #0a1628;">
        PAS-BACKED DEED OF TRUST
      </h2>
      <p style="text-align: center; font-size: 11px; color: #666; margin-bottom: 10px;">
        (Private Mortgage / Credit-Enhanced Transaction)
      </p>
      <p style="text-align: center; font-size: 10px; color: #999; margin-bottom: 30px;">
        <strong>Confidential – For Recording Purposes Only</strong>
      </p>
      
      <div style="background: #f8f9fa; border-left: 4px solid #6366f1; padding: 15px; margin-bottom: 25px;">
        <p style="margin: 0; font-size: 12px;"><strong>Document ID:</strong> ${data.document_id || 'DOT-' + Date.now().toString(36).toUpperCase()}</p>
        <p style="margin: 5px 0 0 0; font-size: 12px;"><strong>Date:</strong> ${today}</p>
        <p style="margin: 5px 0 0 0; font-size: 12px;"><strong>Reference Agreement:</strong> ${data.agreement_id || 'N/A'}</p>
      </div>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px;">1. PARTIES</h3>
      <table style="width: 100%; font-size: 11px; border-collapse: collapse; margin: 15px 0;">
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; width: 35%;"><strong>Trustor / Borrower:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.borrower_name || '___________________________'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Beneficiary / Secured Party:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">Bethereum Private Wealth Management</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Trustee:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.trustee_name || 'Bethereum Trust Services'}</td>
        </tr>
      </table>
      <p style="font-size: 10px; color: #666; margin-top: 10px;">
        This Deed of Trust is executed to secure the obligations of the Trustor under the <strong>Private Mortgage / Finance Agreement</strong> 
        and any related <strong>Performance Assurance Structure (PAS)</strong>.
      </p>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">2. PROPERTY DESCRIPTION</h3>
      <table style="width: 100%; font-size: 11px; border-collapse: collapse; margin: 15px 0;">
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa; width: 35%;"><strong>Property Address:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.property_address || '___________________________'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Legal Description:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.legal_description || 'See attached Schedule A'}</td>
        </tr>
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; background: #f8f9fa;"><strong>County / State:</strong></td>
          <td style="padding: 8px; border: 1px solid #ddd;">${data.property_county || '_______________'} / ${data.property_state || 'Delaware'}</td>
        </tr>
      </table>
      <p style="font-size: 10px; background: #e8f4fd; padding: 10px; border-radius: 4px; margin-top: 10px;">
        The above-described property is the collateral securing the obligations outlined in the Finance Agreement and PAS.
      </p>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">3. SECURED OBLIGATIONS</h3>
      <p style="font-size: 11px; line-height: 1.6; margin-bottom: 10px;">This Deed of Trust secures:</p>
      <ol style="font-size: 11px; line-height: 1.8; margin-left: 20px;">
        <li>The performance of all obligations of the Trustor under the <strong>Private Finance / Mortgage Agreement</strong></li>
        <li>Any rights and obligations under the <strong>Performance Assurance Structure (PAS)</strong></li>
        <li>Any other obligations expressly referenced in this instrument</li>
      </ol>
      <div style="background: #fff3cd; padding: 10px; border-radius: 4px; margin: 15px 0; font-size: 10px;">
        <strong>NOTE:</strong> The PAS provides <strong>conditional, non-payment performance support</strong> and does not replace the Trustor's obligations.
      </div>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">4. TRUSTOR COVENANTS</h3>
      <p style="font-size: 11px; line-height: 1.6; margin-bottom: 10px;">The Trustor agrees to:</p>
      <ul style="font-size: 11px; line-height: 1.8; margin-left: 20px;">
        <li>Maintain the property in good condition</li>
        <li>Pay taxes, insurance, and other encumbrances as required</li>
        <li>Not transfer, sell, or encumber the property without written consent of the Beneficiary</li>
      </ul>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">5. TRUSTEE POWERS</h3>
      <p style="font-size: 11px; line-height: 1.6; margin-bottom: 10px;">
        The Trustee holds legal title for the benefit of the Beneficiary and has the power to:
      </p>
      <ul style="font-size: 11px; line-height: 1.8; margin-left: 20px;">
        <li>Foreclose in accordance with applicable state law upon default</li>
        <li>Release or reconvey title when obligations are satisfied</li>
        <li>Execute documents necessary to perfect, maintain, or enforce the security interest</li>
      </ul>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">6. DEFAULT & REMEDIES</h3>
      <p style="font-size: 11px; line-height: 1.6; margin-bottom: 10px;"><strong>Default occurs upon:</strong></p>
      <ul style="font-size: 11px; line-height: 1.6; margin-left: 20px;">
        <li>Failure to perform obligations under the Finance Agreement or PAS where applicable</li>
        <li>Breach of material covenants regarding the property</li>
      </ul>
      <p style="font-size: 11px; line-height: 1.6; margin: 10px 0;"><strong>Upon default, the Beneficiary may instruct the Trustee to:</strong></p>
      <ul style="font-size: 11px; line-height: 1.6; margin-left: 20px;">
        <li>Foreclose and sell the property under state law</li>
        <li>Apply proceeds to satisfy the secured obligations</li>
        <li>Exercise any other remedies under the Finance Agreement or PAS</li>
      </ul>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">7. PAS ACKNOWLEDGMENT</h3>
      <div style="background: #f8d7da; padding: 12px; border-radius: 4px; font-size: 10px; line-height: 1.6;">
        <p style="margin: 0 0 8px 0;">The Trustor and Beneficiary acknowledge:</p>
        <ul style="margin: 0 0 0 20px; padding: 0;">
          <li>PAS is <strong>conditional and secondary</strong> to the Trustor's obligations</li>
          <li>PAS does <strong>not</strong> constitute a guarantee, surety, or insurance policy</li>
          <li>Obligations under PAS are governed solely by the PAS Agreement</li>
        </ul>
      </div>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">8. GOVERNING LAW</h3>
      <p style="font-size: 11px; line-height: 1.6;">
        This Deed of Trust shall be governed by the laws of the State of <strong>${data.property_state || 'Delaware'}</strong>, 
        without regard to conflict of laws principles.
      </p>
      
      <h3 style="font-size: 13px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">9. EXECUTION & NOTARIZATION</h3>
      <table style="width: 100%; font-size: 11px; border-collapse: collapse; margin-top: 15px;">
        <tr>
          <td style="padding: 10px; width: 50%; vertical-align: top;">
            <strong>Trustor / Borrower:</strong><br/>
            <div style="border-bottom: 1px solid #333; height: 30px; margin: 15px 0 5px 0;"></div>
            ${data.borrower_name || '___________________________'}<br/>
            <span style="font-size: 10px; color: #666;">Date: _____________</span>
          </td>
          <td style="padding: 10px; width: 50%; vertical-align: top;">
            <strong>Beneficiary / Secured Party:</strong><br/>
            <div style="border-bottom: 1px solid #333; height: 30px; margin: 15px 0 5px 0;"></div>
            Bethereum Private Wealth Management<br/>
            <span style="font-size: 10px; color: #666;">Date: _____________</span>
          </td>
        </tr>
        <tr>
          <td style="padding: 10px; vertical-align: top;">
            <strong>Trustee:</strong><br/>
            <div style="border-bottom: 1px solid #333; height: 30px; margin: 15px 0 5px 0;"></div>
            ${data.trustee_name || 'Bethereum Trust Services'}<br/>
            <span style="font-size: 10px; color: #666;">Date: _____________</span>
          </td>
          <td style="padding: 10px; vertical-align: top;">
            <strong>Notary Public:</strong><br/>
            <div style="border-bottom: 1px solid #333; height: 30px; margin: 15px 0 5px 0;"></div>
            _________________________<br/>
            <span style="font-size: 10px; color: #666;">Date: _____________</span>
          </td>
        </tr>
      </table>
      
      <div style="margin-top: 20px; padding: 10px; background: #f8f9fa; border: 1px solid #ddd; border-radius: 4px;">
        <p style="font-size: 10px; margin: 0; color: #666; text-align: center;">
          <strong>RECORDING:</strong> To be recorded in the official land records of the county where the property is located.
        </p>
      </div>
      
      <div style="margin-top: 30px; padding-top: 20px; border-top: 2px solid #6366f1;">
        <p style="font-size: 10px; margin: 0; color: #666; text-align: center;">
          🔒 SECURED DOCUMENT • ✓ BLOCKCHAIN VERIFIED • ⚡ ETHEREUM MAINNET
        </p>
        <p style="font-size: 9px; margin: 5px 0 0 0; color: #999; text-align: center;">
          Generated: ${new Date().toLocaleString()}
        </p>
      </div>
    </div>
  `;
};

// Tokenized Check Issuance & Disclosure Agreement
export const generateTokenizedCheckDisclosure = (data) => {
  const today = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', month: 'long', day: 'numeric' 
  });
  
  return `
    <div class="page" style="background: white; padding: 40px; font-family: 'Times New Roman', serif; color: #1a1a1a; min-height: 1100px;">
      <div style="text-align: center; border-bottom: 3px double #b8860b; padding-bottom: 20px; margin-bottom: 30px;">
        <h1 style="font-size: 18px; margin: 0; letter-spacing: 3px; color: #0a1628;">BETHEREUM PRIVATE WEALTH MANAGEMENT</h1>
        <p style="font-size: 11px; color: #666; margin: 5px 0 0 0;">Private Credit Transactions</p>
      </div>
      
      <h2 style="text-align: center; font-size: 14px; margin-bottom: 5px; color: #0a1628;">
        TOKENIZED & DRAFT CHECK ISSUANCE AND DISCLOSURE AGREEMENT
      </h2>
      <p style="text-align: center; font-size: 11px; color: #666; margin-bottom: 10px;">
        <strong>(Conditional Consideration – Private Credit Transactions)</strong>
      </p>
      <p style="text-align: center; font-size: 10px; color: #999; margin-bottom: 30px;">
        Confidential – Disclosure & Acknowledgment
      </p>
      
      <div style="background: #f8f9fa; border-left: 4px solid #b8860b; padding: 15px; margin-bottom: 25px;">
        <p style="margin: 0; font-size: 12px;"><strong>Document ID:</strong> ${data.document_id || 'TCD-' + Date.now().toString(36).toUpperCase()}</p>
        <p style="margin: 5px 0 0 0; font-size: 12px;"><strong>Date:</strong> ${today}</p>
      </div>
      
      <h3 style="font-size: 12px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px;">1. PURPOSE</h3>
      <p style="font-size: 10px; line-height: 1.6; text-align: justify;">
        This Tokenized & Draft Check Issuance and Disclosure Agreement ("Agreement") governs the issuance, delivery, and use of any 
        <strong>tokenized check, draft check, payment instruction, or similar instrument</strong> provided in connection with a private credit, 
        financing, novation, or settlement transaction.
      </p>
      <p style="font-size: 10px; line-height: 1.6; text-align: justify; background: #fff3cd; padding: 10px; border-radius: 4px; margin-top: 10px;">
        This Agreement exists solely to clarify that such instruments are <strong>conditional evidence of consideration</strong> and <strong>not payment</strong>.
      </p>
      
      <h3 style="font-size: 12px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 20px;">2. PARTIES</h3>
      <table style="width: 100%; font-size: 10px; border-collapse: collapse; margin: 10px 0;">
        <tr>
          <td style="padding: 6px; border: 1px solid #ddd; background: #f8f9fa; width: 40%;"><strong>Issuer / Platform Administrator:</strong></td>
          <td style="padding: 6px; border: 1px solid #ddd;">Bethereum Private Wealth Management</td>
        </tr>
        <tr>
          <td style="padding: 6px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Recipient (Dealer / Lender / Mortgagee):</strong></td>
          <td style="padding: 6px; border: 1px solid #ddd;">${data.lender_name || '___________________________'}</td>
        </tr>
        <tr>
          <td style="padding: 6px; border: 1px solid #ddd; background: #f8f9fa;"><strong>Related Obligor / Borrower:</strong></td>
          <td style="padding: 6px; border: 1px solid #ddd;">${data.borrower_name || '___________________________'}</td>
        </tr>
      </table>
      
      <h3 style="font-size: 12px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 20px;">3. NATURE OF THE INSTRUMENT</h3>
      <p style="font-size: 10px; line-height: 1.6; margin-bottom: 8px;">Any tokenized check, draft check, or payment instruction (the "Instrument") issued:</p>
      <ul style="font-size: 10px; line-height: 1.6; margin: 0 0 0 20px;">
        <li>Is <strong>non-negotiable</strong></li>
        <li>Is <strong>not legal tender</strong></li>
        <li>Is <strong>not payable on demand</strong></li>
        <li>Does <strong>not</strong> constitute a check under UCC Article 3</li>
        <li>Does <strong>not</strong> represent funds on deposit or escrow</li>
      </ul>
      <p style="font-size: 10px; line-height: 1.6; margin-top: 10px; background: #e8f4fd; padding: 8px; border-radius: 4px;">
        The Instrument functions solely as <strong>conditional consideration</strong> pending execution of definitive agreements.
      </p>
      
      <h3 style="font-size: 12px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 20px;">4. CONDITIONS PRECEDENT</h3>
      <p style="font-size: 10px; line-height: 1.6; margin-bottom: 8px;">The Instrument has no force or effect unless and until:</p>
      <ol style="font-size: 10px; line-height: 1.6; margin: 0 0 0 20px;">
        <li>All definitive transaction agreements are fully executed</li>
        <li>Any required acceptance or acknowledgment forms are signed</li>
        <li>Any applicable novation or assumption is approved in writing</li>
        <li>Issuer confirms activation or release conditions</li>
      </ol>
      <p style="font-size: 10px; line-height: 1.6; margin-top: 10px; background: #f8d7da; padding: 8px; border-radius: 4px;">
        <strong>Absent satisfaction of these conditions, the Instrument is void.</strong>
      </p>
      
      <div style="display: flex; gap: 15px; margin-top: 20px;">
        <div style="flex: 1; background: #f8f9fa; padding: 12px; border-left: 3px solid #b8860b;">
          <h4 style="font-size: 11px; color: #0a1628; margin: 0 0 8px 0;">5. RELATIONSHIP TO PAS</h4>
          <p style="font-size: 9px; line-height: 1.5; margin: 0;">Where a PAS exists:</p>
          <ul style="font-size: 9px; line-height: 1.4; margin: 5px 0 0 15px; padding: 0;">
            <li>Instrument does not trigger PAS remedies</li>
            <li>PAS does not guarantee payment</li>
            <li>PAS governed by its own agreement</li>
          </ul>
        </div>
        <div style="flex: 1; background: #f8f9fa; padding: 12px; border-left: 3px solid #b8860b;">
          <h4 style="font-size: 11px; color: #0a1628; margin: 0 0 8px 0;">6. NO SETTLEMENT OR DISCHARGE</h4>
          <p style="font-size: 9px; line-height: 1.5; margin: 0;">Delivery of Instrument:</p>
          <ul style="font-size: 9px; line-height: 1.4; margin: 5px 0 0 15px; padding: 0;">
            <li>Does NOT settle/pay any debt</li>
            <li>Does NOT obligate funding</li>
            <li>Does NOT create payment expectation</li>
          </ul>
        </div>
      </div>
      
      <div style="display: flex; gap: 15px; margin-top: 15px;">
        <div style="flex: 1; background: #f8f9fa; padding: 12px; border-left: 3px solid #b8860b;">
          <h4 style="font-size: 11px; color: #0a1628; margin: 0 0 8px 0;">7. ACCOUNTING & TREATMENT</h4>
          <p style="font-size: 9px; line-height: 1.5; margin: 0;">Recipients acknowledge:</p>
          <ul style="font-size: 9px; line-height: 1.4; margin: 5px 0 0 15px; padding: 0;">
            <li>Not to book as cash/cash equivalent</li>
            <li>Not to treat as payment received</li>
            <li>Retain only for documentation</li>
          </ul>
        </div>
        <div style="flex: 1; background: #f8f9fa; padding: 12px; border-left: 3px solid #b8860b;">
          <h4 style="font-size: 11px; color: #0a1628; margin: 0 0 8px 0;">8. NO BANKING RELATIONSHIP</h4>
          <p style="font-size: 9px; line-height: 1.5; margin: 0;">No party shall construe as creating:</p>
          <ul style="font-size: 9px; line-height: 1.4; margin: 5px 0 0 15px; padding: 0;">
            <li>A banking relationship</li>
            <li>An insurance/surety obligation</li>
            <li>A fiduciary duty</li>
          </ul>
        </div>
      </div>
      
      <h3 style="font-size: 12px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 20px;">9. GOVERNING LAW</h3>
      <p style="font-size: 10px; line-height: 1.6;">
        This Agreement shall be governed by the laws of the <strong>State of Delaware</strong>, without regard to conflicts principles.
      </p>
      
      <h3 style="font-size: 12px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 20px;">10. SIGNATURES</h3>
      <table style="width: 100%; font-size: 10px; border-collapse: collapse; margin-top: 10px;">
        <tr>
          <td style="padding: 8px; width: 33%; vertical-align: top; border: 1px solid #ddd;">
            <strong>Issuer / Platform Administrator:</strong><br/>
            <div style="border-bottom: 1px solid #333; height: 25px; margin: 12px 0 5px 0;"></div>
            Bethereum Private Wealth Management<br/>
            <span style="font-size: 9px; color: #666;">Date: _____________</span>
          </td>
          <td style="padding: 8px; width: 33%; vertical-align: top; border: 1px solid #ddd;">
            <strong>Recipient (Dealer/Lender/Mortgagee):</strong><br/>
            <div style="border-bottom: 1px solid #333; height: 25px; margin: 12px 0 5px 0;"></div>
            ${data.lender_name || '___________________________'}<br/>
            <span style="font-size: 9px; color: #666;">Date: _____________</span>
          </td>
          <td style="padding: 8px; width: 33%; vertical-align: top; border: 1px solid #ddd;">
            <strong>Acknowledged by Borrower:</strong><br/>
            <div style="border-bottom: 1px solid #333; height: 25px; margin: 12px 0 5px 0;"></div>
            ${data.borrower_name || '___________________________'}<br/>
            <span style="font-size: 9px; color: #666;">Date: _____________</span>
          </td>
        </tr>
      </table>
      
      <div style="margin-top: 25px; padding-top: 15px; border-top: 2px solid #b8860b;">
        <p style="font-size: 9px; margin: 0; color: #666; text-align: center;">
          🔒 SECURED DOCUMENT • ✓ BLOCKCHAIN VERIFIED • ⚡ ETHEREUM MAINNET
        </p>
        <p style="font-size: 8px; margin: 5px 0 0 0; color: #999; text-align: center;">
          Generated: ${new Date().toLocaleString()}
        </p>
      </div>
    </div>
  `;
};

// Mortgage Routing Letter for Servicers
export const generateMortgageRoutingLetter = (data) => {
  const today = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', month: 'long', day: 'numeric' 
  });
  
  return `
    <div class="page" style="background: white; padding: 50px 60px; font-family: 'Times New Roman', serif; color: #1a1a1a; min-height: 1100px;">
      <div style="text-align: center; margin-bottom: 40px;">
        <h1 style="font-size: 16px; margin: 0; letter-spacing: 4px; color: #0a1628;">BETHEREUM PRIVATE WEALTH MANAGEMENT</h1>
        <p style="font-size: 10px; color: #6366f1; margin: 5px 0 0 0; letter-spacing: 2px;">Treasury-Indexed Mortgage Obligation (TIMO) Program</p>
      </div>
      
      <div style="margin-bottom: 30px;">
        <p style="font-size: 11px; margin: 0; color: #666;">${today}</p>
        <p style="font-size: 11px; margin: 5px 0 0 0; color: #666;">Reference: ${data.agreement_id || data.loan_number || 'See Enclosed Documents'}</p>
      </div>
      
      <p style="font-size: 12px; margin-bottom: 20px;">Dear Sir or Madam,</p>
      
      <p style="font-size: 11px; line-height: 1.8; text-align: justify; margin-bottom: 20px;">
        This letter serves as a formal <strong>routing and transmittal notice</strong> for the enclosed 
        <strong>Credit-Enhanced Financing / Settlement Package</strong> submitted in connection with the referenced loan obligation.
      </p>
      
      <div style="background: #fff3cd; border: 1px solid #ffc107; padding: 15px 20px; margin: 25px 0; border-radius: 4px;">
        <h3 style="font-size: 12px; color: #856404; margin: 0 0 10px 0; text-transform: uppercase;">⚠️ IMPORTANT ROUTING NOTICE</h3>
        <p style="font-size: 11px; line-height: 1.7; margin: 0; color: #856404;">
          This submission is intended for review by the <strong>beneficial owner of the note</strong>, the 
          <strong>authorized asset manager</strong>, or the <strong>credit committee</strong> with authority to evaluate 
          refinancing, payoff, novation, or structured settlement transactions. If your office functions solely as a 
          loan servicer or payment processor, please <strong>promptly forward this package to the appropriate 
          decision-making department or note holder representative</strong>.
        </p>
      </div>
      
      <h3 style="font-size: 12px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">TRANSACTION OVERVIEW</h3>
      <p style="font-size: 11px; line-height: 1.7; text-align: justify; margin-bottom: 10px;">
        The enclosed package outlines a <strong>private credit-enhanced financing structure</strong>, supported by a 
        Performance Assurance Structure (PAS), and is submitted for consideration as one or more of the following 
        (subject to acceptance):
      </p>
      <ul style="font-size: 11px; line-height: 1.8; margin: 0 0 15px 25px; padding: 0;">
        <li>Refinance or take-out financing</li>
        <li>Conditional payoff or settlement</li>
        <li>Novation or assumption of the existing obligation</li>
        <li>Structured modification supported by private credit enhancement</li>
      </ul>
      <p style="font-size: 10px; color: #666; font-style: italic; margin-bottom: 20px;">
        No representation is made that acceptance is mandatory. This submission is made for <strong>review and consideration only</strong>.
      </p>
      
      <h3 style="font-size: 12px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">ENCLOSED DOCUMENTS (SUMMARY)</h3>
      <table style="width: 100%; font-size: 10px; border-collapse: collapse; margin: 15px 0;">
        <tr>
          <td style="padding: 8px 12px; border: 1px solid #ddd; background: #f8f9fa;">✓</td>
          <td style="padding: 8px 12px; border: 1px solid #ddd;">Executive Transaction Summary</td>
        </tr>
        <tr>
          <td style="padding: 8px 12px; border: 1px solid #ddd; background: #f8f9fa;">✓</td>
          <td style="padding: 8px 12px; border: 1px solid #ddd;">Credit Enhancement / PAS Summary</td>
        </tr>
        <tr>
          <td style="padding: 8px 12px; border: 1px solid #ddd; background: #f8f9fa;">✓</td>
          <td style="padding: 8px 12px; border: 1px solid #ddd;">Private Finance or Mortgage Agreement</td>
        </tr>
        <tr>
          <td style="padding: 8px 12px; border: 1px solid #ddd; background: #f8f9fa;">✓</td>
          <td style="padding: 8px 12px; border: 1px solid #ddd;">Deed of Trust / Security Documentation (if applicable)</td>
        </tr>
        <tr>
          <td style="padding: 8px 12px; border: 1px solid #ddd; background: #f8f9fa;">✓</td>
          <td style="padding: 8px 12px; border: 1px solid #ddd;">UCC-1 Draft or Filing Reference (if applicable)</td>
        </tr>
        <tr>
          <td style="padding: 8px 12px; border: 1px solid #ddd; background: #f8f9fa;">✓</td>
          <td style="padding: 8px 12px; border: 1px solid #ddd;">Acceptance & Acknowledgment Forms</td>
        </tr>
        <tr>
          <td style="padding: 8px 12px; border: 1px solid #ddd; background: #f8f9fa;">✓</td>
          <td style="padding: 8px 12px; border: 1px solid #ddd;">Tokenized Check Disclosure Agreement</td>
        </tr>
      </table>
      
      <h3 style="font-size: 12px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">SERVICER INSTRUCTIONS</h3>
      <p style="font-size: 11px; line-height: 1.7; margin-bottom: 10px;">
        If received by a servicing entity without approval authority, please:
      </p>
      <ol style="font-size: 11px; line-height: 1.8; margin: 0 0 15px 25px; padding: 0;">
        <li>Log receipt to the borrower's account, and</li>
        <li>Forward the complete package to the <strong>note holder, trust administrator, asset manager, or 
        special servicing department</strong> authorized to review structured finance transactions.</li>
      </ol>
      
      <h3 style="font-size: 12px; color: #0a1628; border-bottom: 1px solid #ddd; padding-bottom: 5px; margin-top: 25px;">CONTACT FOR REVIEW</h3>
      <p style="font-size: 11px; line-height: 1.7; margin-bottom: 15px;">
        All communications regarding review, questions, or required clarifications should be directed to the 
        undersigned or the authorized platform representative listed below.
      </p>
      
      <div style="background: #f8d7da; border: 1px solid #f5c6cb; padding: 12px 15px; margin: 20px 0; border-radius: 4px;">
        <p style="font-size: 10px; line-height: 1.6; margin: 0; color: #721c24;">
          <strong>NOTICE:</strong> This submission does <strong>not</strong> constitute legal tender, a unilateral payoff, 
          or a demand for release. It is a <strong>good-faith proposal</strong> for structured resolution through 
          private financing and credit enhancement.
        </p>
      </div>
      
      <p style="font-size: 11px; line-height: 1.7; margin: 25px 0 10px 0;">
        Thank you for your prompt attention and appropriate routing of this matter.
      </p>
      
      <p style="font-size: 11px; margin: 30px 0 5px 0;">Sincerely,</p>
      
      <div style="margin-top: 40px;">
        <div style="border-bottom: 1px solid #333; width: 300px; margin-bottom: 5px;"></div>
        <p style="font-size: 11px; margin: 0;"><strong>Authorized Representative</strong></p>
        <p style="font-size: 11px; margin: 3px 0; color: #666;">Bethereum Private Wealth Management</p>
        <p style="font-size: 10px; margin: 3px 0; color: #666;">Contact: support@bethereum.io</p>
      </div>
      
      <div style="margin-top: 40px; padding-top: 20px; border-top: 2px solid #6366f1;">
        <p style="font-size: 9px; margin: 0; color: #666; text-align: center;">
          🔒 SECURED DOCUMENT • ✓ BLOCKCHAIN VERIFIED • ⚡ ETHEREUM MAINNET
        </p>
        <p style="font-size: 8px; margin: 5px 0 0 0; color: #999; text-align: center;">
          Generated: ${new Date().toLocaleString()}
        </p>
      </div>
    </div>
  `;
};

/**
 * OFFICIAL UCC-1 FINANCING STATEMENT
 * Based on IACA 2023 Revision (Effective 07/01/2023)
 * Tennessee Secretary of State Compliant Format
 * 
 * Fields follow UCC1 standard layout:
 * - Items 1-2: Debtor information
 * - Item 3: Secured Party information  
 * - Item 4: Collateral description
 * - Items 5-7: Special filing information
 * - Items 8, A-C: Optional/Administrative
 */
export const generateOfficialUCC1 = (data) => {
  const filingDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', month: '2-digit', day: '2-digit' 
  });
  const filingNumber = data.filing_number || `TN-${Date.now().toString(36).toUpperCase()}`;
  
  return `
    <div class="page" style="background: white; padding: 30px; font-family: Arial, sans-serif; color: #000; min-height: 1100px; font-size: 10px;">
      <!-- UCC-1 Header -->
      <div style="border: 2px solid #000; margin-bottom: 15px;">
        <div style="display: flex; justify-content: space-between; background: #f5f5f5; padding: 8px 15px; border-bottom: 1px solid #000;">
          <div>
            <strong style="font-size: 14px;">UCC FINANCING STATEMENT</strong>
            <div style="font-size: 9px; color: #666;">FOLLOW INSTRUCTIONS</div>
          </div>
          <div style="text-align: right;">
            <div style="font-size: 9px;">FORM UCC1</div>
            <div style="font-size: 8px; color: #666;">(REV. 07/01/2023)</div>
          </div>
        </div>
        
        <!-- Filing Office Instructions -->
        <div style="padding: 8px 15px; background: #fff; font-size: 9px; border-bottom: 1px solid #ccc;">
          <strong>THE ABOVE SPACE IS FOR FILING OFFICE USE ONLY</strong>
        </div>
        
        <!-- Section 1: DEBTOR #1 -->
        <div style="border-bottom: 1px solid #000;">
          <div style="background: #e8e8e8; padding: 5px 15px; font-weight: bold; font-size: 11px;">
            1. DEBTOR'S NAME: Provide only <u>one</u> Debtor name (1a or 1b) (use exact, full name; do not omit, modify, or abbreviate any part of the Debtor's name)
          </div>
          <div style="padding: 10px 15px;">
            <table style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="width: 20%; padding: 3px 0; font-size: 9px;"><strong>1a. ORGANIZATION'S NAME</strong></td>
                <td style="border-bottom: 1px solid #999; padding: 3px 5px;">${data.debtor_org_name || ''}</td>
              </tr>
            </table>
            <div style="margin-top: 8px; font-size: 9px; color: #666;">OR</div>
            <table style="width: 100%; border-collapse: collapse; margin-top: 5px;">
              <tr>
                <td style="width: 20%; padding: 3px 0; font-size: 9px;"><strong>1b. INDIVIDUAL'S SURNAME</strong></td>
                <td style="border-bottom: 1px solid #999; padding: 3px 5px; width: 30%;">${data.debtor_surname || data.debtor_name?.split(' ').pop() || ''}</td>
                <td style="width: 15%; padding: 3px 5px; font-size: 9px;"><strong>FIRST NAME</strong></td>
                <td style="border-bottom: 1px solid #999; padding: 3px 5px;">${data.debtor_first_name || data.debtor_name?.split(' ')[0] || ''}</td>
              </tr>
              <tr>
                <td style="padding: 3px 0; font-size: 9px;"><strong>ADDITIONAL NAME(S)/INITIAL(S)</strong></td>
                <td style="border-bottom: 1px solid #999; padding: 3px 5px;">${data.debtor_middle || ''}</td>
                <td style="padding: 3px 5px; font-size: 9px;"><strong>SUFFIX</strong></td>
                <td style="border-bottom: 1px solid #999; padding: 3px 5px;">${data.debtor_suffix || ''}</td>
              </tr>
            </table>
            <table style="width: 100%; border-collapse: collapse; margin-top: 8px;">
              <tr>
                <td style="width: 20%; padding: 3px 0; font-size: 9px;"><strong>1c. MAILING ADDRESS</strong></td>
                <td style="border-bottom: 1px solid #999; padding: 3px 5px;">${data.debtor_address || ''}</td>
              </tr>
              <tr>
                <td style="padding: 3px 0; font-size: 9px;"><strong>CITY</strong></td>
                <td style="padding: 3px 5px;">
                  <span style="border-bottom: 1px solid #999; display: inline-block; width: 40%; padding: 0 5px;">${data.debtor_city || ''}</span>
                  <strong style="margin-left: 15px; font-size: 9px;">STATE</strong>
                  <span style="border-bottom: 1px solid #999; display: inline-block; width: 10%; padding: 0 5px; margin-left: 5px;">${data.debtor_state || 'TN'}</span>
                  <strong style="margin-left: 15px; font-size: 9px;">POSTAL CODE</strong>
                  <span style="border-bottom: 1px solid #999; display: inline-block; width: 15%; padding: 0 5px; margin-left: 5px;">${data.debtor_zip || ''}</span>
                </td>
              </tr>
              <tr>
                <td style="padding: 3px 0; font-size: 9px;"><strong>COUNTRY</strong></td>
                <td style="border-bottom: 1px solid #999; padding: 3px 5px;">${data.debtor_country || 'USA'}</td>
              </tr>
            </table>
            <table style="width: 100%; border-collapse: collapse; margin-top: 8px;">
              <tr>
                <td style="width: 60%; padding: 3px 0; font-size: 9px;">
                  <strong>1d. TAX ID#: SSN OR EIN</strong>
                  <span style="border-bottom: 1px solid #999; display: inline-block; width: 40%; padding: 0 5px; margin-left: 10px;">${data.debtor_tax_id || ''}</span>
                </td>
                <td style="padding: 3px 0; font-size: 9px;">
                  <strong>ADD'L INFO RE: ORGANIZATION DEBTOR</strong>
                </td>
              </tr>
              <tr>
                <td colspan="2" style="padding: 3px 0; font-size: 9px;">
                  <strong>1e. TYPE OF ORGANIZATION</strong>
                  <span style="border-bottom: 1px solid #999; display: inline-block; width: 20%; padding: 0 5px; margin-left: 5px;">${data.debtor_org_type || ''}</span>
                  <strong style="margin-left: 15px;">1f. JURISDICTION OF ORGANIZATION</strong>
                  <span style="border-bottom: 1px solid #999; display: inline-block; width: 15%; padding: 0 5px; margin-left: 5px;">${data.debtor_jurisdiction || 'Tennessee'}</span>
                  <strong style="margin-left: 15px;">1g. ORGANIZATIONAL ID #</strong>
                  <span style="border-bottom: 1px solid #999; display: inline-block; width: 15%; padding: 0 5px; margin-left: 5px;">${data.debtor_org_id || ''}</span>
                </td>
              </tr>
            </table>
          </div>
        </div>
        
        <!-- Section 2: DEBTOR #2 (Additional) -->
        <div style="border-bottom: 1px solid #000;">
          <div style="background: #e8e8e8; padding: 5px 15px; font-weight: bold; font-size: 11px;">
            2. ADDITIONAL DEBTOR'S NAME: Provide only <u>one</u> Debtor name (2a or 2b)
          </div>
          <div style="padding: 8px 15px; font-size: 9px; color: #666;">
            [Additional debtor information, if any - use UCC1Ad for more than 2 debtors]
          </div>
        </div>
        
        <!-- Section 3: SECURED PARTY -->
        <div style="border-bottom: 1px solid #000;">
          <div style="background: #e8e8e8; padding: 5px 15px; font-weight: bold; font-size: 11px;">
            3. SECURED PARTY'S NAME (or NAME of ASSIGNEE of ASSIGNOR SECURED PARTY): Provide only <u>one</u> Secured Party name (3a or 3b)
          </div>
          <div style="padding: 10px 15px;">
            <table style="width: 100%; border-collapse: collapse;">
              <tr>
                <td style="width: 20%; padding: 3px 0; font-size: 9px;"><strong>3a. ORGANIZATION'S NAME</strong></td>
                <td style="border-bottom: 1px solid #999; padding: 3px 5px;">${data.secured_party_name || 'Bethereum Private Wealth Management'}</td>
              </tr>
            </table>
            <table style="width: 100%; border-collapse: collapse; margin-top: 8px;">
              <tr>
                <td style="width: 20%; padding: 3px 0; font-size: 9px;"><strong>3c. MAILING ADDRESS</strong></td>
                <td style="border-bottom: 1px solid #999; padding: 3px 5px;">${data.secured_party_address || ''}</td>
              </tr>
              <tr>
                <td style="padding: 3px 0; font-size: 9px;"><strong>CITY</strong></td>
                <td style="padding: 3px 5px;">
                  <span style="border-bottom: 1px solid #999; display: inline-block; width: 40%; padding: 0 5px;">${data.secured_party_city || ''}</span>
                  <strong style="margin-left: 15px; font-size: 9px;">STATE</strong>
                  <span style="border-bottom: 1px solid #999; display: inline-block; width: 10%; padding: 0 5px; margin-left: 5px;">${data.secured_party_state || 'TN'}</span>
                  <strong style="margin-left: 15px; font-size: 9px;">POSTAL CODE</strong>
                  <span style="border-bottom: 1px solid #999; display: inline-block; width: 15%; padding: 0 5px; margin-left: 5px;">${data.secured_party_zip || ''}</span>
                </td>
              </tr>
              <tr>
                <td style="padding: 3px 0; font-size: 9px;"><strong>COUNTRY</strong></td>
                <td style="border-bottom: 1px solid #999; padding: 3px 5px;">${data.secured_party_country || 'USA'}</td>
              </tr>
            </table>
          </div>
        </div>
        
        <!-- Section 4: COLLATERAL -->
        <div style="border-bottom: 1px solid #000;">
          <div style="background: #e8e8e8; padding: 5px 15px; font-weight: bold; font-size: 11px;">
            4. COLLATERAL: This financing statement covers the following collateral:
          </div>
          <div style="padding: 15px; min-height: 120px; font-size: 10px; line-height: 1.5;">
            ${data.collateral_description || `All BETH tokens (ERC-20) now owned or hereafter acquired by Debtor, contract address 0x6822824c5349bf005e353ec5dda3fa34595ea811 on Ethereum Mainnet; all rights to receive, transfer, exchange, or otherwise use or dispose of such tokens; all rights under the Credit Enhancement Agreement and Performance Assurance Structure (PAS) dated ${data.agreement_date || filingDate}; all tokenized checks or payment instruments issued thereunder; all accounts, general intangibles, and payment intangibles arising therefrom; and any substitutions, replacements, accessions, or proceeds thereof.`}
            <br/><br/>
            <strong>Maximum Principal Indebtedness:</strong> $${data.max_indebtedness || '0'}<br/>
            <strong>BETH Collateral Amount:</strong> ${data.beth_collateral || '0'} BETH
          </div>
        </div>
        
        <!-- Sections 5-7: Check boxes -->
        <div style="border-bottom: 1px solid #000; display: flex;">
          <div style="flex: 1; border-right: 1px solid #000; padding: 8px 15px; font-size: 9px;">
            <strong>5.</strong> Check <u>only if</u> applicable and check <u>only one</u> box:<br/>
            ☐ Debtor is a Transmitting Utility<br/>
            ☐ Filed in connection with a Manufactured-Home Transaction — effective 30 years<br/>
            ☐ Filed in connection with a Public-Finance Transaction — effective 30 years
          </div>
          <div style="flex: 1; padding: 8px 15px; font-size: 9px;">
            <strong>6.</strong> Check <u>only if</u> applicable and check <u>only one</u> box:<br/>
            ☐ Agricultural Lien<br/>
            ☐ Non-UCC Filing<br/>
            <br/>
            <strong>7.</strong> ALTERNATIVE DESIGNATION (if applicable):<br/>
            ☐ Lessee/Lessor ☐ Consignee/Consignor ☐ Bailee/Bailor ☐ Licensee/Licensor<br/>
            ☐ Seller/Buyer ☐ Assignor/Assignee
          </div>
        </div>
        
        <!-- Section 8: Optional Filer Reference -->
        <div style="border-bottom: 1px solid #000; padding: 8px 15px; font-size: 9px;">
          <strong>8. OPTIONAL FILER REFERENCE DATA:</strong>
          <span style="margin-left: 10px;">${data.filer_reference || filingNumber}</span>
        </div>
        
        <!-- Administrative Sections A, B, C -->
        <div style="display: flex; border-bottom: 1px solid #000;">
          <div style="flex: 1; border-right: 1px solid #000; padding: 8px 15px; font-size: 9px;">
            <strong>A. NAME AND PHONE OF CONTACT AT SUBMITTER:</strong><br/>
            ${data.submitter_name || 'Bethereum Support'}<br/>
            ${data.submitter_phone || ''}
          </div>
          <div style="flex: 1; padding: 8px 15px; font-size: 9px;">
            <strong>B. E-MAIL CONTACT AT SUBMITTER:</strong><br/>
            ${data.submitter_email || 'support@bethereum.io'}
          </div>
        </div>
        
        <div style="padding: 8px 15px; font-size: 9px;">
          <strong>C. SEND ACKNOWLEDGMENT TO: (Name and Address)</strong><br/>
          ${data.acknowledgment_name || data.debtor_name || ''}<br/>
          ${data.acknowledgment_address || data.debtor_address || ''}<br/>
          ${data.debtor_city || ''}, ${data.debtor_state || 'TN'} ${data.debtor_zip || ''}
        </div>
      </div>
      
      <!-- Footer with filing info -->
      <div style="margin-top: 15px; padding: 15px; background: #f8f8f8; border: 1px solid #ddd; font-size: 9px;">
        <div style="display: flex; justify-content: space-between;">
          <div>
            <strong>FILING OFFICE:</strong> Tennessee Secretary of State<br/>
            Division of Business Services - UCC<br/>
            312 Rosa L. Parks Avenue, 6th Floor<br/>
            Nashville, TN 37243
          </div>
          <div style="text-align: right;">
            <strong>Filing Number:</strong> ${filingNumber}<br/>
            <strong>Filing Date:</strong> ${filingDate}<br/>
            <strong>Status:</strong> ${data.filing_status || 'PENDING SUBMISSION'}
          </div>
        </div>
        <div style="margin-top: 10px; padding-top: 10px; border-top: 1px dashed #ccc; text-align: center; color: #666;">
          <strong>NOTICE:</strong> Under Section 9-516(b)(5)(C), filing office rules require name of secured party of record if this is an amendment.
          <br/>For questions regarding this filing, contact the secured party at the address shown in Item 3.
        </div>
      </div>
      
      <!-- Blockchain Verification -->
      <div style="margin-top: 10px; text-align: center; font-size: 8px; color: #999;">
        🔒 SECURED FILING • ✓ BLOCKCHAIN VERIFIED • ⚡ ETHEREUM MAINNET<br/>
        BETH Token Contract: 0x6822824c5349bf005e353ec5dda3fa34595ea811<br/>
        Generated: ${new Date().toLocaleString()}
      </div>
    </div>
  `;
};

export default {
  generateAutoSubmissionMemorandum,
  generateAutoAcceptanceForm,
  generateMortgageSubmissionMemorandum,
  generateMortgageAcceptanceForm,
  generateMortgageDeedOfTrust,
  generateTokenizedCheckDisclosure,
  generateMortgageRoutingLetter,
  generateOfficialUCC1
};
